import { route } from "quasar/wrappers";
import {
  createRouter,
  createMemoryHistory,
  createWebHistory,
  createWebHashHistory,
} from "vue-router";
import routes from "./routes";
import { onboardingTestActive } from "src/utils/onboardingOverride";

/*
 * If not building with SSR mode, you can
 * directly export the Router instantiation;
 *
 * The function below can be async too; either use
 * async/await or return a Promise which resolves
 * with the Router instance.
 */

const ALLOWED_ROUTES_DURING_ONBOARDING = [
  "/login",
  "/register",
  "/forgot-password",
  "/my/company/pricing-plans",
  "/pricing-plans",
  "/",
];

function requiresOnboarding(store, route) {
  const user = store.state.auth.user;
  if (!user?.id) return false;

  const completed = localStorage.getItem("onboarding_completed") === "true";
  if (completed) return false;

  const companyPlans = store.state.pricingWizard?.companyPlans || [];
  const hasMembership = companyPlans.some(
    (p) => (p.type?.code || p.type_code) === "membership"
  );

  const companyFromAuth = store.state.auth?.company;
  const companyFromData =
    store.state.data?.company || store.state.current?.company;
  const companyMembershipPlan =
    companyFromAuth?.membership_plan || companyFromData?.membership_plan;

  const hasFromCompany =
    !!companyMembershipPlan?.id &&
    companyFromData?.membership_payment_failed !== true;

  const hasAnyMembership = hasMembership || hasFromCompany;

  if (hasAnyMembership) {
    localStorage.setItem("onboarding_completed", "true");
    return false;
  }

  const optedIn = onboardingTestActive(route);
  return !hasAnyMembership || optedIn;
}

export default route(function ({ store }) {
  const createHistory = process.env.SERVER
    ? createMemoryHistory
    : process.env.VUE_ROUTER_MODE === "history"
    ? createWebHistory
    : createWebHashHistory;

  const Router = createRouter({
    scrollBehavior: () => ({ left: 0, top: 0 }),
    routes,

    // Leave this as is and make changes in quasar.conf.js instead!
    // quasar.conf.js -> build -> vueRouterMode
    // quasar.conf.js -> build -> publicPath
    history: createHistory(
      process.env.MODE === "ssr" ? void 0 : process.env.VUE_ROUTER_BASE
    ),
  });

  Router.beforeEach((to, from, next) => {
    // Check onboarding first
    if (store.state.auth.user?.id) {
      const isAllowedRoute = ALLOWED_ROUTES_DURING_ONBOARDING.some((route) =>
        to.path.startsWith(route)
      );

      if (!isAllowedRoute && requiresOnboarding(store, to)) {
        return next({
          path: "/my/company/pricing-plans",
          query: { onboarding: "1", redirect: to.path },
        });
      }
    }

    // Then check role requirements
    const requiredRole = to.meta.requiredRole;
    if (requiredRole && store.state.auth.user?.role?.code !== requiredRole) {
      next("/403");
    } else {
      next();
    }
  });

  return Router;
});
